<section class="asset-mappings-page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Hotspot asset mappings</p>
      <h2>Review bus mapping matches</h2>
      <p>Correct Powerlynx location and hotspot mappings before operator-scoped passenger access is enabled.</p>
    </div>
    <div class="context-note">
      <i class="material-icons">info</i>
      <span>Powerlynx location maps to the operator. Powerlynx hotspot maps to the bus. Router identity is shown for diagnostics.</span>
    </div>
  </div>

  <div class="alert success" *ngIf="successMessage">
    <i class="material-icons">check_circle</i>
    <span>{{ successMessage }}</span>
  </div>

  <div class="mappings-layout">
    <section class="mappings-panel">
      <div class="panel-toolbar">
        <div>
          <p class="eyebrow">Mappings</p>
          <h3>{{ getPageSummary() }}</h3>
        </div>
        <div class="filter-row">
          <label class="search-box">
            <i class="material-icons">search</i>
            <input type="search" placeholder="Search location, hotspot, router, operator, bus" [(ngModel)]="searchTerm" (keyup.enter)="applyFilters()">
          </label>
          <input class="provider-filter" type="text" placeholder="Provider" [(ngModel)]="providerFilter" (keyup.enter)="applyFilters()">
          <label class="toggle-filter">
            <input type="checkbox" [(ngModel)]="unresolvedOnly" (ngModelChange)="applyFilters()">
            Unresolved
          </label>
          <label class="toggle-filter">
            <input type="checkbox" [(ngModel)]="unverifiedOnly" (ngModelChange)="applyFilters()">
            Unverified
          </label>
          <button class="filter-action" type="button" (click)="applyFilters()">
            <i class="material-icons">filter_alt</i>
            <span>Apply</span>
          </button>
          <label class="remap-control">
            <span>Remap limit</span>
            <select [(ngModel)]="remapLimit">
              <option *ngFor="let limit of remapLimitOptions" [ngValue]="limit">{{ limit }}</option>
            </select>
          </label>
          <button class="filter-action" type="button" [disabled]="isRemapping" (click)="remapExistingSessions()">
            <i class="material-icons">manage_history</i>
            <span>{{ isRemapping ? 'Remapping...' : 'Remap existing sessions' }}</span>
          </button>
        </div>
      </div>

      <div class="inline-error" *ngIf="errorMessage">{{ errorMessage }}</div>
      <div class="inline-error" *ngIf="remapError">{{ remapError }}</div>
      <div class="remap-summary" *ngIf="remapResult">
        <span><strong>{{ remapResult.reviewed }}</strong> reviewed</span>
        <span><strong>{{ remapResult.remappedSessions }}</strong> remapped sessions</span>
        <span><strong>{{ remapResult.createdAssetMappings }}</strong> created mappings</span>
        <span><strong>{{ remapResult.errors }}</strong> errors</span>
      </div>

      <div class="loading-state" *ngIf="isLoading">
        <i class="material-icons">sync</i>
        <span>Loading hotspot asset mappings...</span>
      </div>

      <div class="table-scroll" *ngIf="!isLoading && mappings.length; else noMappings">
        <table>
          <thead>
            <tr>
              <th>Powerlynx location</th>
              <th>Powerlynx hotspot</th>
              <th>Router identity</th>
              <th>Organisation</th>
              <th>Vehicle</th>
              <th>State</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let mapping of mappings; trackBy: trackMapping"
                [class.selected]="selectedMapping?.hotspotAssetMappingID === mapping.hotspotAssetMappingID"
                [class.unresolved]="isUnresolved(mapping)"
                (click)="selectMapping(mapping)">
              <td>
                <strong>{{ formatExternalId(mapping.externalLocationID) }}</strong>
                <small>{{ mapping.provider }}</small>
              </td>
              <td>
                <strong>{{ formatExternalId(mapping.externalHotspotID) }}</strong>
                <small *ngIf="mapping.vehicleCode">legacy bus code {{ mapping.vehicleCode }}</small>
              </td>
              <td>
                <strong>{{ getRouterIdentityLabel(mapping) }}</strong>
                <small>{{ isDefaultRouterIdentity(mapping) ? 'default router identity' : 'router identity' }}</small>
              </td>
              <td>
                <strong>{{ mapping.organisationName || getOrganisationName(mapping.organisationID) }}</strong>
                <small>ID {{ mapping.organisationID || '-' }} <span *ngIf="getOrganisationPowerlynxLocationID(mapping.organisationID)">/ location {{ getOrganisationPowerlynxLocationID(mapping.organisationID) }}</span></small>
              </td>
              <td>
                <strong>{{ mapping.vehicleIdentificationNumber || 'Unresolved bus' }}</strong>
                <small>{{ mapping.vehicleRegistration || '-' }} <span *ngIf="mapping.vehicleLabel">/ {{ mapping.vehicleLabel }}</span></small>
              </td>
              <td>
                <span class="status-badge" [class.verified]="mapping.isVerified" [class.unverified]="!mapping.isVerified">
                  {{ mapping.isVerified ? 'admin-confirmed' : 'auto-resolved' }}
                </span>
                <span class="status-badge unresolved-badge" *ngIf="isUnresolved(mapping)">unresolved</span>
              </td>
              <td>
                <span>{{ formatDate(mapping.lastSeenAt) }}</span>
                <small>first {{ formatDate(mapping.firstSeenAt) }}</small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noMappings>
        <div class="empty-state" *ngIf="!isLoading && !hasActiveFilters()">
          <i class="material-icons">hub</i>
          <h3>No asset mappings yet</h3>
          <p>Mappings will appear after hotspot sessions create Powerlynx location and hotspot records.</p>
        </div>
        <div class="empty-state" *ngIf="!isLoading && hasActiveFilters()">
          <i class="material-icons">filter_alt_off</i>
          <h3>No mappings match the filters</h3>
          <p>Try clearing search, provider, unresolved, or unverified filters.</p>
          <button class="filter-action" type="button" (click)="clearFilters()">
            <i class="material-icons">close</i>
            <span>Clear filters</span>
          </button>
        </div>
      </ng-template>

      <div class="pagination-bar" *ngIf="!isLoading && totalPages > 1">
        <button class="secondary-action" type="button" [disabled]="page === 1" (click)="changePage(page - 1)">
          <i class="material-icons">chevron_left</i>
          <span>Previous</span>
        </button>
        <span>Page {{ page }} of {{ totalPages }}</span>
        <button class="secondary-action" type="button" [disabled]="page === totalPages" (click)="changePage(page + 1)">
          <span>Next</span>
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
    </section>

    <aside class="detail-panel" *ngIf="isLoadingDetail">
      <div class="empty-state">
        <i class="material-icons">sync</i>
        <h3>Loading mapping</h3>
        <p>Fetching the selected hotspot asset mapping.</p>
      </div>
    </aside>

    <aside class="detail-panel" *ngIf="!isLoadingDetail && selectedMapping">
      <div class="detail-header">
        <div>
          <p class="eyebrow">Mapping detail</p>
          <h3>Location {{ formatExternalId(selectedMapping.externalLocationID) }} / Hotspot {{ formatExternalId(selectedMapping.externalHotspotID) }}</h3>
          <span class="record-id">Mapping {{ selectedMapping.hotspotAssetMappingID }}</span>
        </div>
        <span class="status-badge" [class.verified]="selectedMapping.isVerified" [class.unverified]="!selectedMapping.isVerified">
          {{ selectedMapping.isVerified ? 'admin-confirmed' : 'auto-resolved' }}
        </span>
      </div>

      <div class="inline-error detail-error" *ngIf="detailError">{{ detailError }}</div>

      <dl class="detail-grid">
        <div>
          <dt>Powerlynx location</dt>
          <dd>{{ formatExternalId(selectedMapping.externalLocationID) }}</dd>
        </div>
        <div>
          <dt>Powerlynx hotspot</dt>
          <dd>{{ formatExternalId(selectedMapping.externalHotspotID) }}</dd>
        </div>
        <div>
          <dt>Router identity</dt>
          <dd>
            {{ getRouterIdentityLabel(selectedMapping) }}
            <small>{{ isDefaultRouterIdentity(selectedMapping) ? 'Default MikroTik identity, not an error by itself.' : 'Diagnostic router identity.' }}</small>
          </dd>
        </div>
        <div>
          <dt>Legacy parsed codes</dt>
          <dd>{{ selectedMapping.operatorCode || '-' }} / {{ selectedMapping.vehicleCode || '-' }}</dd>
        </div>
        <div>
          <dt>Resolved organisation</dt>
          <dd>
            {{ selectedMapping.organisationName || getOrganisationName(selectedMapping.organisationID) }}
            <small *ngIf="getOrganisationPowerlynxLocationID(selectedMapping.organisationID)">Powerlynx location {{ getOrganisationPowerlynxLocationID(selectedMapping.organisationID) }}</small>
          </dd>
        </div>
        <div>
          <dt>Resolved bus</dt>
          <dd>{{ selectedMapping.vehicleIdentificationNumber || 'Unresolved bus' }}</dd>
        </div>
      </dl>

      <form class="mapping-form" (ngSubmit)="saveMapping()">
        <label>
          Organisation
          <select [(ngModel)]="editOrganisationID" name="organisationID" (ngModelChange)="onOrganisationSelected()">
            <option [ngValue]="null">Unresolved organisation</option>
            <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
              {{ getOrganisationOptionLabel(organisation) }}
            </option>
          </select>
          <small *ngIf="editOrganisationID && getOrganisationPowerlynxLocationID(editOrganisationID)">
            This organisation uses Powerlynx location {{ getOrganisationPowerlynxLocationID(editOrganisationID) }} for hotspot session mapping.
          </small>
          <small *ngIf="editOrganisationID && !getOrganisationPowerlynxLocationID(editOrganisationID) && getOrganisationOperatorCode(editOrganisationID)">
            Legacy fallback operator code: {{ getOrganisationOperatorCode(editOrganisationID) }}.
          </small>
        </label>

        <label>
          Bus
          <select [(ngModel)]="editVehicleID" name="vehicleID" (ngModelChange)="onVehicleSelected()">
            <option [ngValue]="null">Unresolved bus</option>
            <option *ngFor="let vehicle of getFilteredVehicles(); trackBy: trackVehicle" [ngValue]="vehicle.vehicleID">
              {{ getVehicleLabel(vehicle) }}
            </option>
          </select>
          <small *ngIf="getSelectedVehicle() as vehicle">
            Selected bus belongs to {{ getOrganisationName(vehicle.organisationID) }}. Vehicle identification number is shown first for manual review.
          </small>
        </label>

        <label class="verified-toggle">
          <input type="checkbox" [(ngModel)]="editIsVerified" name="isVerified">
          Mark this mapping as verified/admin-corrected
        </label>

        <button class="primary-action" type="submit" [disabled]="isSaving">
          <i class="material-icons">save</i>
          <span>{{ isSaving ? 'Saving...' : 'Save mapping' }}</span>
        </button>
      </form>
    </aside>

    <aside class="detail-panel" *ngIf="!isLoadingDetail && !selectedMapping">
      <div class="empty-state">
        <i class="material-icons">directions_bus</i>
        <h3>No mapping selected</h3>
        <p>Select a Powerlynx location/hotspot mapping to correct the resolved organisation or bus.</p>
      </div>
    </aside>
  </div>
</section>
